import React from 'react';
import { Transaction, Category, Account } from '../../types/budget';
import { IconButton } from '../ui/Button';
import styles from './TransactionItem.module.css';

interface TransactionItemProps {
  transaction: Transaction;
  category: Category | null;
  account: Account | null;
  onEdit: () => void;
  isSelectMode?: boolean;
  isSelected?: boolean;
  onSelectionToggle?: () => void;
}

/**
 * Format amount for display
 * Shows absolute value with color coding
 */
const formatAmount = (amount: number): string => {
  const absAmount = Math.abs(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absAmount);
};

/**
 * Format date for compact display (MM/DD)
 * Handles both YYYY-MM-DD format and ISO strings from backend
 */
const formatDateCompact = (dateStr: string): string => {
  let date: Date;
  
  // Check if already in YYYY-MM-DD format (no time component)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // Append time to avoid timezone issues
    date = new Date(dateStr + 'T00:00:00');
  } else {
    // Parse as ISO string (e.g., "2024-12-30T00:00:00.000Z")
    date = new Date(dateStr);
  }
  
  // Fallback if date is invalid
  if (isNaN(date.getTime())) {
    return '--/--';
  }
  
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
};

/**
 * Get category abbreviation (first 3-4 chars)
 */
const getCategoryAbbr = (name: string): string => {
  // Handle special cases
  const specialCases: Record<string, string> = {
    'Uncategorized': 'UNCAT',
    'Restaurants': 'REST',
    'Grocery': 'GROC',
    'Amazon & Target': 'A&T',
    'Travel': 'TRVL',
    'Medical': 'MED',
    'Phone/Internet/TV': 'TEL',
    'Uber & Lyft': 'RIDE',
    'Food Delivery': 'DLVR',
    'Tuition & Childcare': 'CHLD',
    'Home & Garden Maintenance': 'HOME',
    'Gas/Electric/Water/Trash': 'UTIL',
    'Home & Auto Insurance': 'INS',
    'Life Insurance': 'LIFE',
    'Property Tax': 'PTAX',
    'Income Tax': 'ITAX',
    'Other Shopping': 'SHOP',
    'Mortgage': 'MORT',
    'Car': 'CAR',
    'Earnings (Post-Withholding)': 'EARN',
  };

  if (specialCases[name]) {
    return specialCases[name];
  }

  // Default: first 4 letters uppercase
  return name.substring(0, 4).toUpperCase();
};

/**
 * Get account abbreviation (first 3-4 chars)
 */
const getAccountAbbr = (name: string): string => {
  // Handle common patterns
  const words = name.split(/\s+/);
  if (words.length >= 2) {
    // Use initials for multi-word names (e.g., "Chase Sapphire" -> "CS")
    return words.map(w => w[0]).join('').toUpperCase().substring(0, 4);
  }
  // Single word: first 4 letters
  return name.substring(0, 4).toUpperCase();
};

export const TransactionItem: React.FC<TransactionItemProps> = ({
  transaction,
  category,
  account,
  onEdit,
  isSelectMode = false,
  isSelected = false,
  onSelectionToggle,
}) => {
  const isExpense = transaction.amount < 0;
  const hasAttachment = transaction.attachments && transaction.attachments.length > 0;

  const handleClick = () => {
    if (isSelectMode && onSelectionToggle) {
      onSelectionToggle();
    } else {
      onEdit();
    }
  };

  const handleSelectionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className={`${styles.transactionItem} ${isSelectMode ? styles.selectMode : ''} ${isSelected ? styles.selected : ''}`}
      onClick={handleClick}
      data-id={transaction.id}
    >
      {/* Selection checkbox - only visible in select mode */}
      {isSelectMode && (
        <input
          type="checkbox"
          className={styles.selectionCheckbox}
          checked={isSelected}
          onChange={() => onSelectionToggle?.()}
          onClick={handleSelectionClick}
          aria-label={`Select transaction: ${transaction.description}`}
        />
      )}

      {/* Date */}
      <span className={styles.date}>{formatDateCompact(transaction.date)}</span>

      {/* Account indicator */}
      {account && (
        <span
          className={styles.accountBadge}
          style={{
            backgroundColor: account.color,
          }}
          title={account.name}
        >
          <span className={styles.accountAbbr}>
            {getAccountAbbr(account.name)}
          </span>
        </span>
      )}

      {/* Category indicator */}
      <span
        className={styles.categoryBadge}
        style={{
          backgroundColor: category ? category.color : '#8B949E',
        }}
        title={category ? category.name : 'Uncategorized'}
      >
        <span className={styles.categoryAbbr}>
          {category ? getCategoryAbbr(category.name) : 'UNCAT'}
        </span>
      </span>

      {/* Description */}
      <span className={styles.description} title={transaction.description}>
        {transaction.description}
      </span>

      {/* Attachment indicator */}
      {hasAttachment && (
        <span className={styles.attachmentIcon} title="Has attachment">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14.5 7.5L8.35355 13.6464C7.15829 14.8417 5.24171 14.8417 4.04645 13.6464C2.85118 12.4512 2.85118 10.5346 4.04645 9.33934L10.1929 3.19289C10.9739 2.41184 12.2403 2.41184 13.0214 3.19289C13.8024 3.97394 13.8024 5.24026 13.0214 6.02131L7.22487 11.8178C6.85829 12.1844 6.26608 12.1844 5.8995 11.8178C5.53292 11.4513 5.53292 10.8591 5.8995 10.4925L11.3464 5.04558"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </span>
      )}

      {/* Amount */}
      <span
        className={`${styles.amount} ${isExpense ? styles.expense : styles.income}`}
      >
        {isExpense ? '-' : '+'}{formatAmount(transaction.amount)}
      </span>

      {/* Edit button - hidden in select mode */}
      {!isSelectMode && (
        <IconButton
          icon={
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11.7071 2.29289C12.0976 2.68342 12.0976 3.31658 11.7071 3.70711L4.70711 10.7071C4.51957 10.8946 4.26522 11 4 11H3C2.44772 11 2 10.5523 2 10V9C2 8.73478 2.10536 8.48043 2.29289 8.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289L11.7071 2.29289Z"
                fill="currentColor"
              />
              <path
                d="M2 13C2 12.4477 2.44772 12 3 12H13C13.5523 12 14 12.4477 14 13C14 13.5523 13.5523 14 13 14H3C2.44772 14 2 13.5523 2 13Z"
                fill="currentColor"
              />
            </svg>
          }
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          variant="ghost"
          size="sm"
          aria-label="Edit transaction"
          className={styles.editButton}
        />
      )}
    </div>
  );
};

export default TransactionItem;
